import CryptoJS from 'crypto-js';
import { AUTH_ROLES } from 'globalconfig/constants/keys';
import { BACKEND_DATE_FORMAT_2 } from 'globalconfig/constants/static';
import { filter, forEach, get, isEqual, map } from 'lodash';
import moment from 'moment';
import { getAuthDetails, hasRestrictedRole } from './commonUtils';
import { phoneRegExp } from './constants';
export * from './commonUtils';

const serviceProvider = window.$environment?.SERVICE_PROVIDER;
const appLayout = window.$environment.CURRENT_LAYOUT;

export const addParentProducer = (name = 'producerCode') => {
  if (!sessionStorage.getItem('resellerId')) {
    return {};
  }
  return {
    [name]: sessionStorage.getItem('resellerId'),
  };
};

export const getBase64Url = (file) => {
  return new Promise((resolve, reject) => {
    var reader = new FileReader();
    reader.onloadend = function () {
      resolve(reader.result);
    };
    reader.onerror = function (error) {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

export const getParentProducerId = (name = 'producerCode') => {
  if (!sessionStorage.getItem('resellerId')) {
    return '';
  }
  return `?${name}=${sessionStorage.getItem('resellerId')}`;
};

export const isSubReseller = () => {
  const auth = getAuthDetails();
  return hasRestrictedRole([AUTH_ROLES['RESELLER_ADMIN']]) && auth?.parentProducerCode;
};

export const isNonSubReseller = () => {
  const auth = getAuthDetails();
  return hasRestrictedRole([AUTH_ROLES['RESELLER_ADMIN']]) && !auth?.parentProducerCode;
};

export const hasEnabledConfiguration = (module) => {
  const { settings } = getAuthDetails();
  return settings?.[module] || false;
};

export const getLayout = () => {
  if (window.$environment?.IS_CSV_TOOL) {
    return 'SimpleUi';
  }
  if (isAdminPortal()) {
    return 'Admin';
  }
  return window?.$environment?.CURRENT_LAYOUT;
};

export const isAdminPortal = () => {
  return window.location.href.includes('console');
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const modifyRequest = (req) => {
  let newReq = { ...req };
  newReq.page = Math.floor((Number(req.displayStart) + Number(req.displayLength)) / Number(req.displayLength));
  newReq.size = req.displayLength * 1;
  delete newReq.displayLength;
  delete newReq.displayStart;
  return newReq;
};

export const modifyDailyLogRequest = (req) => {
  let newReq = { ...req };
  if (req?.sortCol) newReq.sortColumn = req?.sortCol;
  delete newReq.sortCol;
  if (req?.sortDir) newReq.sortDirection = (req?.sortDir || '').toUpperCase();
  delete newReq.sortDir;
  if (req?.displayStart > -1)
    newReq.pageNumber = req?.displayStart < req?.displayLength ? 1 : req?.displayStart / req?.displayLength + 1;
  delete newReq.displayStart;
  if (req?.resellerId) newReq.producerCode = req?.resellerId;
  delete newReq.resellerId;
  if (req?.startDate) newReq.fromDate = moment(req?.startDate).format(BACKEND_DATE_FORMAT_2);
  delete newReq.startDate;
  if (req?.endDate) newReq.toDate = moment(req?.endDate).format(BACKEND_DATE_FORMAT_2);
  delete newReq.endDate;
  return newReq;
};

export const mapObjectToArray = (obj) => {
  return Object.entries(obj).map(([name, value]) => ({ label: name, value }));
};

export const phoneNumberValidation = (inputVal) => {
  if (inputVal.substring(0, 12).match(phoneRegExp)) {
    return inputVal.substring(0, 12);
  } else {
    const number = /^[0-9]+$/;
    let value = inputVal.split('-').join('');
    const isValid = value.match(number);
    let updatedValue = isValid ? value.match(/.{1,3}/g).join('-') : value.split(value.length, 0);
    updatedValue =
      updatedValue.length >= 13
        ? updatedValue.slice(0, updatedValue.length - 2)
        : updatedValue?.length > 0
        ? updatedValue?.substring(0, 13)
        : updatedValue;
    return updatedValue || '';
  }
};

export const objectArrayFromKeyValuePair = (list, isTier = false) => {
  let newArray = [];
  forEach(list, (value, key) => {
    newArray.push({
      label: key,
      value: value?.name,
      ...value,
      ...(isTier ? { isDisabled: !value?.masterTier } : {}),
    });
  });
  return newArray;
};

export const loadStyleMore = (ParentStyled, url) => {
  try {
    const style = require(`@/${url}/${window?.$environment?.CURRENT_LAYOUT}`)?.default;
    return { ...ParentStyled, ...style };
  } catch (error) {
    return ParentStyled;
  }
};

export const loadDefaultStyle = (ParentStyled, url) => {
  try {
    const style = require(`@generated/${url}/${window?.$environment?.CURRENT_LAYOUT}`)?.default;
    return { ...ParentStyled, ...style };
  } catch (error) {
    return loadStyleMore(ParentStyled, url);
  }
};

export const loadStyle = (ParentStyled, url) => {
  try {
    const style = require(`@generated/${url}/Admin`)?.default;
    return { ...ParentStyled, ...style };
  } catch (error) {
    const style = loadDefaultStyle(ParentStyled, url);
    return style;
  }
};

export const loadMorePageConfig = () => {
  try {
    return require(`@/config/pages`);
  } catch (error) {
    return require(`@/config/pages/${serviceProvider}`);
  }
};

export const loadPageConfig = () => {
  try {
    return require(`@/config/pages/${serviceProvider}/${appLayout}`);
  } catch (error) {
    const style = loadMorePageConfig();
    return style;
  }
};

export const scrollToTop = () => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
};

export const scrollIntoView = (name = '') => {
  const ele = document.getElementById(name);
  ele && ele.scrollIntoView({ behavior: 'auto', block: 'center' });
};

export const getFilteredTierForCompany = (subscriptionTiers = [], currentResellerData = null, values = {}, parentReseller) => {
  if (hasRestrictedRole([AUTH_ROLES?.ADMIN])) {
    return [
      ...filter(subscriptionTiers, (e) => !e?.isDisabled),
      ...map(
        filter(
          subscriptionTiers,
          (e) =>
            e?.isDisabled &&
            isEqual(get(currentResellerData, 'defaultTier'), get(e, 'value')) &&
            get(currentResellerData, 'defaultTier') &&
            isEqual(get(currentResellerData, 'producerCode' === get(values, 'producerCode')))
        ),
        (e) => ({ ...e, isDisabled: false })
      ),
    ];
  }
  if (parentReseller) {
    return [
      ...filter(subscriptionTiers, (e) => e?.masterTier),
      ...map(
        filter(
          subscriptionTiers,
          (e) => !e?.masterTier && get(parentReseller, 'defaultTier') && get(parentReseller, 'defaultTier') === e?.value
        ),
        (e) => ({ ...e, isDisabled: false })
      ),
    ];
  }
  return [...subscriptionTiers];
};

export const getFilteredTierForReseller = (subscriptionTiers = [], currentResellerData = null, value = {}) => {
  if (hasRestrictedRole([AUTH_ROLES?.ADMIN])) {
    return subscriptionTiers;
  }
  if (currentResellerData) {
    return [
      ...filter(subscriptionTiers, (e) => e?.masterTier),
      ...map(
        filter(
          subscriptionTiers,
          (e) =>
            !e?.masterTier &&
            get(currentResellerData, 'defaultTier') &&
            isEqual(get(currentResellerData, 'defaultTier'), get(e, 'value')) &&
            get(currentResellerData, 'defaultTier') === e?.value
        ),
        (e) => ({ ...e, isDisabled: false })
      ),
    ];
  }
  return [...subscriptionTiers];
};
const secretKey = 'URL_SECRET_!@#';

export const encryptParams = (text = '') => {
  const encryptText = CryptoJS.AES.encrypt(text, secretKey).toString().replaceAll('=', 'X00X').replaceAll('+', 'Z00Z');
  return encryptText;
};

export const decryptParams = (encryptedText = '') => {
  const text1 = (encryptedText || '').replaceAll('X00X', '=').replaceAll('Z00Z', '+');
  const bytes = CryptoJS.AES.decrypt(text1, secretKey);
  const text = bytes.toString(CryptoJS.enc.Utf8);
  return text;
};

export const setSearchParams = (obj) => {
  const params = new URLSearchParams(window.location.search);

  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      params.set(key, encryptParams(obj[key]?.value) || '');
    }
  }

  window.history.replaceState(null, '', '?' + params.toString());
};

export const getSearchParams = () => {
  const params = new URLSearchParams(window.location.search);
  const result = {};
  params.forEach((value, key) => {
    result[key] = decryptParams(value) || '';
  });
  return result;
};

const PASSWORD_LENGTH = 12;
const getRandomChar = (charset) => charset[Math.floor(Math.random() * charset.length)];
export const generatePassword = () => {
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%^&*()_+~';
  const all = lower + upper + numbers + special;
  let password = [getRandomChar(lower), getRandomChar(upper), getRandomChar(numbers), getRandomChar(special)];
  for (let i = password.length; i < PASSWORD_LENGTH; i++) {
    password.push(getRandomChar(all));
  }
  for (let i = password.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [password[i], password[j]] = [password[j], password[i]];
  }
  return password.join('');
};

export const passwordStrength = (password = '') => {
  const length = password.length;
  const regexPsd = [/[a-z]/, /[A-Z]/, /[0-9]/, /[!@#$%^&*()_+~\-=[\]{};':"\\|,.<>/?]/];
  const matches = regexPsd.reduce((idx, regex) => idx + regex.test(password), 0);
  if (matches >= 3 && length >= 12) return 'Strong';
  if (matches >= 2 && length >= 8) return 'Medium';
  return 'Weak';
};

export const isDriver = () => {
  const auth = getAuthDetails(false);
  if (auth && auth.roles && auth.roles.includes('DRIVER')) return true;
  return false;
};

export const isdvirOnlyUser = () => {
  const auth = getAuthDetails(false);
  if (auth && auth.enterpriseFeatureDto && auth.enterpriseFeatureDto?.dvirOnlyUserEnabled) return true;
  return false;
};
